/**
 * <shipping-protection> — the cart drawer's shipping protection toggle.
 *
 * Behaviour, per the customizer settings:
 * - Toggle ON  : the product is NOT added to the cart while browsing. It is
 *                added on the way to checkout, so the customer never sees it
 *                as a line item but does pay for it.
 * - Toggle OFF : nothing is added, and if the product is in the cart it is
 *                removed immediately.
 *
 * Cart membership is always read live from /cart.js rather than trusted from
 * the server-rendered data-in-cart attribute. That attribute is a snapshot of
 * the last render and goes stale — most importantly when the customer returns
 * from checkout via the back button and the page comes out of the bfcache,
 * which previously let an unticked toggle leave the product in the cart.
 *
 * With no product assigned in the customizer the widget is display only: the
 * toggle still moves the shown subtotal but nothing touches the cart.
 *
 * The drawer re-renders by replacing .drawer__inner's innerHTML, so this
 * element is destroyed and recreated on every cart change. The toggle state is
 * kept in localStorage so it survives that rather than snapping back on.
 */
if (!customElements.get("shipping-protection")) {
	const STORAGE_KEY = "straand:shipping-protection"

	const shopRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/"

	const readStoredState = () => {
		try {
			return window.localStorage.getItem(STORAGE_KEY)
		} catch (error) {
			// Private mode / blocked storage — fall back to the default.
			return null
		}
	}

	const writeStoredState = (value) => {
		try {
			window.localStorage.setItem(STORAGE_KEY, String(value))
		} catch (error) {
			/* not fatal, the toggle just won't persist */
		}
	}

	customElements.define(
		"shipping-protection",
		class ShippingProtection extends HTMLElement {
			connectedCallback() {
				this.checkbox = this.querySelector("[data-shipping-protection]")
				if (!this.checkbox) return

				this.variantId = this.dataset.variantId || null

				const stored = readStoredState()
				if (stored !== null) this.checkbox.checked = stored === "true"

				this.checkbox.addEventListener("change", this.onToggle.bind(this))
				this.bindCheckout()

				this.syncSubtotal()

				// Reconcile against the real cart on load, so an unticked toggle
				// never leaves the product behind from an earlier session.
				if (!this.checkbox.checked) this.reconcile({ render: true })
			}

			/** Live membership check — never trust the rendered snapshot. */
			async isInCart() {
				if (!this.variantId) return false

				try {
					const cart = await fetch(`${shopRoot()}cart.js`).then((response) => response.json())
					return cart.items.some((item) => String(item.variant_id) === String(this.variantId))
				} catch (error) {
					return false
				}
			}

			/** Drops the product if the toggle is off and it is actually in the cart. */
			async reconcile({ render }) {
				if (this.checkbox.checked || !this.variantId) return false
				if (!(await this.isInCart())) return false

				await this.removeFromCart({ render })
				return true
			}

			async onToggle() {
				writeStoredState(this.checkbox.checked)

				if (await this.reconcile({ render: true })) return

				this.syncSubtotal()
			}

			syncSubtotal() {
				const subtotal = document.querySelector("[data-cart-subtotal]")
				if (!subtotal) return

				subtotal.textContent = this.checkbox.checked ? subtotal.dataset.subtotalProtected : subtotal.dataset.subtotalBase
			}

			get drawer() {
				return document.querySelector("cart-drawer")
			}

			async removeFromCart({ render }) {
				const drawer = this.drawer
				const body = { id: this.variantId, quantity: 0 }

				if (render && drawer) {
					body.sections = drawer.getSectionsToRender().map((section) => section.id)
					body.sections_url = window.location.pathname
				}

				const response = await fetch(routes.cart_change_url, { ...fetchConfig(), body: JSON.stringify(body) })
				const parsedState = await response.json()

				if (render && drawer) drawer.renderContents(parsedState)
			}

			async addToCart() {
				await fetch(routes.cart_add_url, {
					...fetchConfig(),
					body: JSON.stringify({ items: [{ id: this.variantId, quantity: 1 }] }),
				})
			}

			bindCheckout() {
				const checkoutButton = document.getElementById("CartDrawer-Checkout")
				if (!checkoutButton) return

				checkoutButton.addEventListener("click", this.onCheckout.bind(this))
			}

			async onCheckout(event) {
				// Display-only mode: let the form submit untouched.
				if (!this.variantId) return

				event.preventDefault()

				// Settle the cart against the toggle before leaving, so the
				// customer never arrives at checkout with protection they removed
				// (or without protection they asked for).
				try {
					const present = await this.isInCart()

					if (this.checkbox.checked && !present) {
						await this.addToCart()
					} else if (!this.checkbox.checked && present) {
						await this.removeFromCart({ render: false })
					}
				} catch (error) {
					/* fall through to checkout regardless — never trap the customer */
				}

				this.submitCheckout()
			}

			submitCheckout() {
				const form = document.getElementById("CartDrawer-Form")
				if (!form) {
					window.location.href = `${shopRoot()}checkout`
					return
				}

				// form.submit() skips the submit button, so the `checkout` parameter
				// the cart form needs has to be added by hand.
				if (!form.querySelector('input[name="checkout"]')) {
					const checkoutInput = document.createElement("input")
					checkoutInput.type = "hidden"
					checkoutInput.name = "checkout"
					form.appendChild(checkoutInput)
				}

				form.submit()
			}
		}
	)
}
